/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Product,
  Category,
  SpecialOffer,
  StoreInfo,
  SearchParams,
  PaginatedResponse,
  ApiResponse,
} from "../types";
import { deviceService } from "./deviceService";

class ApiService {
  private baseURL: string;
  private appId: string | null;
  private teamId: string | null;

  constructor() {
    this.baseURL = import.meta.env.VITE_API_URL || "http://localhost:3000/api";
    this.appId = import.meta.env.VITE_APP_ID || null;
    this.teamId = import.meta.env.VITE_TEAM_ID || null;
  }

  // A read carries the device id once registration has given one, and goes out
  // without it otherwise: the store is public, and a visitor whose registration
  // failed must still see it.
  private async fetchAPI<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    const deviceId = deviceService.getKnownDeviceId();

    const response = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        "X-App-ID": this.appId || "",
        "X-Team-ID": this.teamId || "",
        ...(deviceId && { "X-Device-ID": deviceId }),
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.statusText}`);
    }

    return response.json();
  }

  // Get categories filtered by type=product
  async getCategories(): Promise<ApiResponse<Category[]>> {
    try {
      if (!this.appId || !this.teamId) {
        return {
          success: false,
          error:
            "App ID and Team ID are required. Set VITE_APP_ID and VITE_TEAM_ID environment variables.",
        };
      }

      const queryParams = new URLSearchParams({
        type: "product",
        limit: "100",
      });

      const response = await this.fetchAPI<{
        success: boolean;
        data: any[];
        pagination?: any;
      }>(`/public/categories?${queryParams.toString()}`);

      console.log("response", response);

      if (!response.success || !response.data) {
        return {
          success: false,
          error: "Failed to fetch categories",
        };
      }

      // Map API category to benipaltoys.com Category format
      const categories: Category[] = response.data.map((cat: any) => ({
        id: String(cat.id),
        name: cat.name,
        description: cat.description || "",
        icon: cat.icon || "📦",
        productCount: cat.productCount || 0, // API now returns productCount directly
        featured: false, // Default to false, can be updated if API provides this
      }));

      return {
        success: true,
        data: categories,
      };
    } catch (error: any) {
      console.error("Error fetching categories:", error);
      return {
        success: false,
        error: error.message || "Failed to fetch categories",
      };
    }
  }

  // Get products with filters and pagination
  async getProducts(
    params?: SearchParams
  ): Promise<PaginatedResponse<Product>> {
    try {
      if (!this.appId || !this.teamId) {
        return {
          success: false,
          error:
            "App ID and Team ID are required. Set VITE_APP_ID and VITE_TEAM_ID environment variables.",
          pagination: {
            page: params?.page || 1,
            limit: params?.limit || 12,
            total: 0,
            totalPages: 0,
          },
        };
      }

      const queryParams = new URLSearchParams();

      if (params?.query) {
        queryParams.append("search", params.query);
      }

      if (params?.filters?.category) {
        queryParams.append("categoryId", params.filters.category);
      }

      if (params?.filters?.priceMin !== undefined) {
        queryParams.append("minPrice", String(params.filters.priceMin));
      }

      if (params?.filters?.priceMax !== undefined) {
        queryParams.append("maxPrice", String(params.filters.priceMax));
      }

      if (params?.filters?.inStock !== undefined) {
        queryParams.append("inStock", String(params.filters.inStock));
      }

      if (params?.filters?.featured !== undefined) {
        queryParams.append("isFeatured", String(params.filters.featured));
      }

      if (params?.sortBy) {
        queryParams.append("sortBy", params.sortBy);
      }

      if (params?.sortOrder) {
        queryParams.append("sortOrder", params.sortOrder);
      }

      queryParams.append("page", String(params?.page || 1));
      queryParams.append("limit", String(params?.limit || 12));

      const response = await this.fetchAPI<{
        success: boolean;
        data: any[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      }>(`/public/products?${queryParams.toString()}`);

      if (!response.success || !response.data) {
        return {
          success: false,
          error: "Failed to fetch products",
          pagination: {
            page: params?.page || 1,
            limit: params?.limit || 12,
            total: 0,
            totalPages: 0,
          },
        };
      }

      // Map API products to benipaltoys.com Product format
      const products: Product[] = response.data.map((p: any) => ({
        id: String(p.id),
        name: p.name,
        description: p.description || "",
        price: p.basePrice || 0,
        originalPrice: p.discountPercentage
          ? p.basePrice / (1 - p.discountPercentage / 100)
          : undefined,
        image: p.images?.[0]?.url || "",
        images: p.images?.map((img: any) => img.url) || [],
        category: p.category?.name || "",
        categoryId: p.categoryId ? String(p.categoryId) : "",
        rating: 0, // Not available in API
        reviewCount: 0, // Not available in API
        ageRange: "", // Not available in API
        inStock: (p.currentStock || 0) > 0,
        stockCount: p.currentStock || 0,
        featured: p.isFeatured || false,
        onSale: !!p.discountPercentage && p.discountPercentage > 0,
        tags: p.tags || [],
        createdAt: p.createdAt ? new Date(p.createdAt) : new Date(),
        updatedAt: p.updatedAt ? new Date(p.updatedAt) : new Date(),
      }));

      return {
        success: true,
        data: products,
        pagination: response.pagination,
      };
    } catch (error: any) {
      console.error("Error fetching products:", error);
      return {
        success: false,
        error: error.message || "Failed to fetch products",
        pagination: {
          page: params?.page || 1,
          limit: params?.limit || 12,
          total: 0,
          totalPages: 0,
        },
      };
    }
  }

  // Get single product by ID
  async getProduct(id: string): Promise<ApiResponse<Product>> {
    try {
      if (!this.appId || !this.teamId) {
        return {
          success: false,
          error:
            "App ID and Team ID are required. Set VITE_APP_ID and VITE_TEAM_ID environment variables.",
        };
      }

      const response = await this.fetchAPI<{
        success: boolean;
        data: any;
      }>(`/public/products/${id}`);

      if (!response.success || !response.data) {
        return {
          success: false,
          error: "Product not found",
        };
      }

      const p = response.data;
      const product: Product = {
        id: String(p.id),
        name: p.name,
        description: p.description || "",
        price: p.basePrice || 0,
        originalPrice: p.discountPercentage
          ? p.basePrice / (1 - p.discountPercentage / 100)
          : undefined,
        image: p.images?.[0]?.url || "",
        images: p.images?.map((img: any) => img.url) || [],
        category: p.category?.name || "",
        categoryId: p.categoryId ? String(p.categoryId) : "",
        rating: 0,
        reviewCount: 0,
        ageRange: "",
        inStock: (p.currentStock || 0) > 0,
        stockCount: p.currentStock || 0,
        featured: p.isFeatured || false,
        onSale: !!p.discountPercentage && p.discountPercentage > 0,
        tags: p.tags || [],
        createdAt: p.createdAt ? new Date(p.createdAt) : new Date(),
        updatedAt: p.updatedAt ? new Date(p.updatedAt) : new Date(),
      };

      return {
        success: true,
        data: product,
      };
    } catch (error: any) {
      console.error("Error fetching product:", error);
      return {
        success: false,
        error: error.message || "Failed to fetch product",
      };
    }
  }

  // Get featured products
  async getFeaturedProducts(): Promise<ApiResponse<Product[]>> {
    try {
      const response = await this.getProducts({
        filters: { featured: true },
        limit: 20,
      });

      if (!response.success) {
        return {
          success: false,
          error: response.error || "Failed to fetch featured products",
        };
      }

      return {
        success: true,
        data: response.data || [],
      };
    } catch (error: any) {
      console.error("Error fetching featured products:", error);
      return {
        success: false,
        error: error.message || "Failed to fetch featured products",
      };
    }
  }

  // Get special offers
  async getSpecialOffers(
    featured?: boolean
  ): Promise<ApiResponse<SpecialOffer[]>> {
    try {
      if (!this.appId || !this.teamId) {
        return {
          success: false,
          error:
            "App ID and Team ID are required. Set VITE_APP_ID and VITE_TEAM_ID environment variables.",
        };
      }

      const queryParams = new URLSearchParams();
      if (featured !== undefined) {
        queryParams.append("featured", String(featured));
      }

      const response = await this.fetchAPI<{
        success: boolean;
        data: any[];
        pagination?: any;
      }>(`/public/offers?${queryParams.toString()}`);

      if (!response.success || !response.data) {
        return {
          success: false,
          error: "Failed to fetch special offers",
        };
      }

      // Map API response to frontend SpecialOffer type
      const offers: SpecialOffer[] = response.data.map((offer: any) => ({
        id: String(offer.id),
        title: offer.title,
        description: offer.description,
        discountPercentage: offer.discountPercentage,
        image: offer.image,
        validUntil: new Date(offer.validUntil),
        categoryIds: offer.categoryIds || [],
        featured: offer.featured || false,
      }));

      return {
        success: true,
        data: offers,
      };
    } catch (error: any) {
      console.error("Error fetching special offers:", error);
      return {
        success: false,
        error: error.message || "Failed to fetch special offers",
      };
    }
  }

  // Get YouTube cached data
  async getYouTubeData(): Promise<
    ApiResponse<{
      channelInfo: any;
      videos: any[];
      lastRefreshedAt: string | null;
      refreshError: string | null;
    }>
  > {
    try {
      if (!this.appId || !this.teamId) {
        return {
          success: false,
          error:
            "App ID and Team ID are required. Set VITE_APP_ID and VITE_TEAM_ID environment variables.",
        };
      }

      const response = await this.fetchAPI<{
        success: boolean;
        data: any;
      }>(`/public/youtube`);

      if (!response.success || !response.data) {
        return {
          success: false,
          error: "Failed to fetch YouTube data",
        };
      }

      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      console.error("Error fetching YouTube data:", error);
      return {
        success: false,
        error: error.message || "Failed to fetch YouTube data",
      };
    }
  }

  // Get store information
  async getStoreInfo(): Promise<ApiResponse<StoreInfo>> {
    try {
      if (!this.appId || !this.teamId) {
        return {
          success: false,
          error:
            "App ID and Team ID are required. Set VITE_APP_ID and VITE_TEAM_ID environment variables.",
        };
      }

      const response = await this.fetchAPI<{
        success: boolean;
        data: any;
      }>(`/public/store`);

      if (!response.success || !response.data) {
        return {
          success: false,
          error: "Failed to fetch store information",
        };
      }

      // Map API response to frontend StoreInfo type
      const storeInfo: StoreInfo = {
        name: response.data.name,
        address: {
          street: response.data.address?.street || "",
          city: response.data.address?.city || "",
          state: response.data.address?.state || "",
          zipCode: response.data.address?.zipCode || "",
          country: response.data.address?.country || "",
        },
        phone: response.data.phone || "",
        phone2: response.data.phone2 || "",
        email: response.data.email || "",
        hours: {
          weekdays: response.data.hours?.weekdays || "",
          weekends: response.data.hours?.weekends || "",
        },
        socialMedia: {
          instagram: response.data.socialMedia?.instagram || "",
          youtube: response.data.socialMedia?.youtube || "",
          whatsappChannel: response.data.socialMedia?.whatsappChannel || "",
        },
      };

      return {
        success: true,
        data: storeInfo,
      };
    } catch (error: any) {
      console.error("Error fetching store information:", error);
      return {
        success: false,
        error: error.message || "Failed to fetch store information",
      };
    }
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
